import { useEffect, useMemo, useRef, useState } from 'react';
import { CHAT_REPORT_RESOLUTION_PRESETS } from '../../config/constants';
import apiClient from '../../utils/api';
import { COOWN_PROJECT_START_YEAR } from '../../config/constants';
import BrowseListedDateFilter from '../listings/BrowseListedDateFilter';
import AdminReviewLogPanel from '../forms/AdminReviewLogPanel';
import PlatformActivityChart from './PlatformActivityChart';
import PaginationBar from '../shared/PaginationBar';

const STATUS_FILTERS = [
  { key: 'open', label: 'Open', icon: 'fa-flag' },
  { key: 'reviewing', label: 'Reviewing', icon: 'fa-search' },
  { key: 'resolved', label: 'Resolved', icon: 'fa-check-circle' },
  { key: 'dismissed', label: 'Dismissed', icon: 'fa-archive' },
  { key: 'all', label: 'All reports', icon: 'fa-list' },
];

const STATUS_METRICS = [
  { key: 'open', label: 'Open' },
  { key: 'reviewing', label: 'Reviewing' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'dismissed', label: 'Dismissed' },
];

const STATUS_CHART_COLORS = {
  open: '#dc3545',
  reviewing: '#d4b06f',
  resolved: '#28a745',
  dismissed: '#64748b',
};

const CATEGORY_LABELS = {
  fraud: 'Fraud or scam',
  suspicious_listing: 'Suspicious listing',
  harassment: 'Harassment or abuse',
  spam: 'Spam',
  other: 'Other',
};

const STATUS_LABELS = {
  open: 'Open',
  reviewing: 'Reviewing',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

const PAGE_SIZE = 10;

const formatTimestamp = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const formatShortDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const statusBadgeClass = (status) => {
  if (status === 'resolved') return 'status-badge status-approved';
  if (status === 'dismissed') return 'status-badge status-rejected';
  if (status === 'reviewing') return 'status-badge status-pending';
  return 'status-badge status-pending';
};

const formatReviewerLabel = (row) => {
  if (row.reviewerName && row.reviewerEmail) return `${row.reviewerName} (${row.reviewerEmail})`;
  return row.reviewerEmail || row.reviewerName || 'Admin';
};

const needsResolutionForm = (status) => status === 'resolved' || status === 'dismissed';

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

const resolveReportParticipants = (report) => {
  if (!report) {
    return {
      reporterEmail: '',
      reporterRole: '',
      reportedEmail: '',
      reportedRole: '',
      reportedTitle: 'Reported user',
    };
  }

  const reporterEmail = report.reporterEmail || '';
  const reporterNorm = normalizeEmail(reporterEmail);
  const buyerNorm = normalizeEmail(report.buyerEmail);
  const agentNorm = normalizeEmail(report.agentEmail);

  let reportedEmail = '';
  let reportedRole = '';
  let reportedTitle = 'Reported user';
  let reporterRole = 'participant';

  if (reporterNorm && buyerNorm && reporterNorm === buyerNorm) {
    reporterRole = 'buyer';
    reportedEmail = report.agentEmail || '';
    reportedRole = 'agent';
    reportedTitle = 'Reported · Listing agent';
  } else if (reporterNorm && agentNorm && reporterNorm === agentNorm) {
    reporterRole = 'agent';
    reportedEmail = report.buyerEmail || '';
    reportedRole = 'buyer';
    reportedTitle = 'Reported · Buyer';
  } else if (report.reporterUid && report.buyerUid && report.reporterUid === report.buyerUid) {
    reporterRole = 'buyer';
    reportedEmail = report.agentEmail || '';
    reportedRole = 'agent';
    reportedTitle = 'Reported · Listing agent';
  } else if (report.reporterUid && report.agentUid && report.reporterUid === report.agentUid) {
    reporterRole = 'agent';
    reportedEmail = report.buyerEmail || '';
    reportedRole = 'buyer';
    reportedTitle = 'Reported · Buyer';
  } else if (agentNorm && reporterNorm !== agentNorm) {
    reportedEmail = report.agentEmail || '';
    reportedRole = 'agent';
    reportedTitle = 'Reported · Listing agent';
  } else if (buyerNorm && reporterNorm !== buyerNorm) {
    reportedEmail = report.buyerEmail || '';
    reportedRole = 'buyer';
    reportedTitle = 'Reported · Buyer';
  }

  return {
    reporterEmail,
    reporterRole,
    reportedEmail,
    reportedRole,
    reportedTitle,
  };
};

const getMessageParticipantRole = (msg, participants) => {
  const sender = normalizeEmail(msg.senderEmail);
  const reporter = normalizeEmail(participants.reporterEmail);
  const reported = normalizeEmail(participants.reportedEmail);

  if (sender && reporter && sender === reporter) return 'reporter';
  if (sender && reported && sender === reported) return 'reported';
  if (msg.senderRole === participants.reporterRole && participants.reporterRole) return 'reporter';
  if (msg.senderRole === participants.reportedRole && participants.reportedRole) return 'reported';
  return msg.senderRole || 'participant';
};

const PARTICIPANT_ROLE_LABELS = {
  reporter: 'Reporter',
  reported: 'Reported',
  buyer: 'Buyer',
  agent: 'Agent',
  participant: 'Participant',
};

const ReviewNotesModal = ({ action, busy, onClose, onSubmit }) => {
  const [notes, setNotes] = useState('');
  const [resolutionType, setResolutionType] = useState('');
  const [blockReportedUser, setBlockReportedUser] = useState(false);

  useEffect(() => {
    if (!action) return;
    setNotes('');
    setResolutionType('');
    setBlockReportedUser(false);
  }, [action?.id, action?.status]);

  if (!action) return null;

  const titleByStatus = {
    reviewing: 'Start review',
    resolved: 'Resolve report',
    dismissed: 'Dismiss report',
    open: 'Re-open report',
  };

  const showResolutionForm = needsResolutionForm(action.status);
  const selectedPreset = CHAT_REPORT_RESOLUTION_PRESETS.find((p) => p.value === resolutionType);

  const handlePresetChange = (value) => {
    setResolutionType(value);
    const preset = CHAT_REPORT_RESOLUTION_PRESETS.find((p) => p.value === value);
    if (preset?.text) setNotes(preset.text);
    if (preset?.blockUser) setBlockReportedUser(true);
    else if (value !== 'account_blocked') setBlockReportedUser(false);
  };

  const handleSubmit = () => {
    if (showResolutionForm && !resolutionType) return;
    if (showResolutionForm && !notes.trim()) return;
    onSubmit({
      notes: notes.trim(),
      resolutionType: showResolutionForm ? resolutionType : '',
      blockReportedUser: showResolutionForm ? blockReportedUser : false,
    });
  };

  const submitDisabled = busy
    || (showResolutionForm && (!resolutionType || !notes.trim()));

  return (
    <div className="modal-overlay active" onClick={(e) => e.target === e.currentTarget && !busy && onClose?.()}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="admin-report-notes-title">
        <div className="modal-header">
          <h2 id="admin-report-notes-title">{titleByStatus[action.status] || 'Update report'}</h2>
          <button type="button" className="modal-close" disabled={busy} onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="modal-body">
          {showResolutionForm ? (
            <>
              <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', marginBottom: '1rem', lineHeight: 1.5 }}>
                Choose an outcome and write your conclusions. Other admins can read this in the review history. The reporter may receive an email depending on the outcome.
              </p>
              <div className="form-group">
                <label htmlFor="admin-report-resolution-preset">Outcome</label>
                <select
                  id="admin-report-resolution-preset"
                  value={resolutionType}
                  disabled={busy}
                  onChange={(e) => handlePresetChange(e.target.value)}
                  style={{ width: '100%', padding: '0.6rem', borderRadius: 8, border: '1px solid var(--border)' }}
                >
                  <option value="">Select an outcome…</option>
                  {CHAT_REPORT_RESOLUTION_PRESETS.map((preset) => (
                    <option key={preset.value} value={preset.value}>{preset.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="admin-report-notes-input">Review conclusions</label>
                <textarea
                  id="admin-report-notes-input"
                  rows={5}
                  value={notes}
                  disabled={busy}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Summarize what you reviewed and your decision…"
                  style={{ width: '100%', padding: '0.6rem', borderRadius: 8, border: '1px solid var(--border)', fontFamily: 'inherit' }}
                />
              </div>
              {selectedPreset?.blockUser || resolutionType === 'account_blocked' ? (
                <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  <input
                    type="checkbox"
                    checked={blockReportedUser}
                    disabled={busy}
                    onChange={(e) => setBlockReportedUser(e.target.checked)}
                    style={{ marginTop: '0.2rem' }}
                  />
                  <span>
                    Block the reported user&apos;s account. They will receive an email about the policy violation, and the reporter will be notified.
                  </span>
                </label>
              ) : null}
            </>
          ) : (
            <>
              <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', marginBottom: '1rem', lineHeight: 1.5 }}>
                Add an internal note for other admins. This is saved on the report and shown in the review history.
              </p>
              <div className="form-group">
                <label htmlFor="admin-report-notes-input">Review notes (optional)</label>
                <textarea
                  id="admin-report-notes-input"
                  rows={4}
                  value={notes}
                  disabled={busy}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="What did you check? Any follow-up needed?"
                  style={{ width: '100%', padding: '0.6rem', borderRadius: 8, border: '1px solid var(--border)', fontFamily: 'inherit' }}
                />
              </div>
            </>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-outline" disabled={busy} onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary btn-sm" disabled={submitDisabled} onClick={handleSubmit}>
              {busy ? 'Saving…' : (action.status === 'resolved' ? 'Resolve' : action.status === 'dismissed' ? 'Dismiss' : 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReportDetailModal = ({
  report,
  detail,
  detailLoading,
  emailConfigured,
  resendingEmail,
  onClose,
  onResendEmail,
  renderActions,
}) => {
  if (!report) return null;
  const activeDetail = detail?.id === report.id ? detail : null;
  const messages = activeDetail?.messages || [];
  const messagesSource = activeDetail?.messagesSource || 'none';
  const emailLog = activeDetail?.reporterEmailLog || report.reporterEmailLog;
  const participants = resolveReportParticipants(report);

  return (
    <div className="modal-overlay active" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="modal admin-chat-report-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="chat-report-detail-title">
        <div className="admin-chat-report-modal__header">
          <div className="admin-chat-report-modal__header-main">
            <span className={statusBadgeClass(report.status)}>{STATUS_LABELS[report.status] || report.status}</span>
            <h2 id="chat-report-detail-title">{report.propertyTitle || 'Listing conversation'}</h2>
            <p className="admin-chat-report-modal__subtitle">
              {CATEGORY_LABELS[report.category] || report.category}
              {' · '}
              {formatTimestamp(report.createdAt)}
              {activeDetail?.propertyLocation ? ` · ${activeDetail.propertyLocation}` : ''}
            </p>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="modal-body admin-chat-report-modal__body">
          <div className="admin-chat-report-participants">
            <div className="admin-chat-report-participant admin-chat-report-participant--reporter">
              <span className="admin-chat-report-participant__label">
                <i className="fas fa-flag" aria-hidden="true"></i> Reporter
              </span>
              <strong>{participants.reporterEmail || '—'}</strong>
              <span className="admin-chat-report-participant__hint">Filed this report</span>
            </div>
            <div className="admin-chat-report-participant admin-chat-report-participant--reported">
              <span className="admin-chat-report-participant__label">
                <i className="fas fa-user-shield" aria-hidden="true"></i> {participants.reportedTitle}
              </span>
              <strong>{participants.reportedEmail || '—'}</strong>
              <span className="admin-chat-report-participant__hint">Subject of the report</span>
            </div>
          </div>

          <div className="admin-chat-report-reason">
            <span className="admin-chat-report-reason__label">Report reason</span>
            <p>{report.reason}</p>
          </div>

          <div className="admin-chat-report-section">
            <div className="admin-chat-report-section__head">
              <h3><i className="fas fa-comments"></i> Chat transcript</h3>
              {messagesSource !== 'none' ? (
                <span className="admin-chat-report-section__meta">
                  {messagesSource === 'snapshot' ? 'Snapshot at report time' : 'Live thread'}
                </span>
              ) : null}
            </div>
            <div className="admin-chat-report-legend">
              <span><i className="admin-chat-report-legend__dot admin-chat-report-legend__dot--reporter"></i> Reporter</span>
              <span><i className="admin-chat-report-legend__dot admin-chat-report-legend__dot--reported"></i> Reported user</span>
            </div>
            {detailLoading && !activeDetail ? (
              <p className="admin-chat-report-loading"><i className="fas fa-spinner fa-spin"></i> Loading transcript…</p>
            ) : null}
            {activeDetail ? (
              <div className="admin-chat-report-thread admin-chat-report-thread--admin">
                {messages.length === 0 ? (
                  <div className="listing-chat-empty">
                    <i className="fas fa-comments" aria-hidden="true"></i>
                    <p>No messages in this conversation.</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const role = getMessageParticipantRole(msg, participants);
                    const isReporter = role === 'reporter';
                    const roleLabel = PARTICIPANT_ROLE_LABELS[role] || 'Participant';
                    return (
                      <div
                        key={msg.id}
                        className={`admin-chat-report-message${isReporter ? ' admin-chat-report-message--reporter' : role === 'reported' ? ' admin-chat-report-message--reported' : ''}`}
                      >
                        <div className="admin-chat-report-message__head">
                          <span className={`admin-chat-report-role admin-chat-report-role--${role}`}>
                            {roleLabel}
                          </span>
                          <span className="admin-chat-report-message__who">
                            {msg.senderName || msg.senderEmail || 'Participant'}
                          </span>
                          <span className="admin-chat-report-message__time">{formatTimestamp(msg.createdAt)}</span>
                        </div>
                        <div className="admin-chat-report-message__text">{msg.text}</div>
                      </div>
                    );
                  })
                )}
              </div>
            ) : null}
          </div>

          {(report.reviewedAt || report.reviewNotes || report.adminReviewLog?.length) ? (
            <div className="admin-chat-report-section">
              <div className="admin-chat-report-section__head">
                <h3><i className="fas fa-clipboard-check"></i> Admin review</h3>
              </div>
              {(report.reviewedAt || report.reviewNotes) ? (
                <div className="admin-chat-report-review admin-chat-report-review--modal">
                  <div className="admin-chat-report-review__meta">
                    {formatTimestamp(report.reviewedAt)}
                    {' · '}
                    {formatReviewerLabel(report)}
                  </div>
                  {report.reviewNotes ? <p>{report.reviewNotes}</p> : null}
                </div>
              ) : null}
              {report.adminReviewLog?.length ? (
                <AdminReviewLogPanel
                  sub={{ adminReviewLog: report.adminReviewLog }}
                  hint="Full history for other admins."
                />
              ) : null}
            </div>
          ) : null}

          {(report.status === 'resolved' || report.status === 'dismissed') ? (
            <div className={`admin-chat-report-email-status${emailConfigured ? '' : ' admin-chat-report-email-status--warn'}`}>
              <div className="admin-chat-report-section__head">
                <h3><i className="fas fa-envelope"></i> Reporter notification</h3>
              </div>
              {!emailConfigured ? (
                <p className="admin-chat-report-email-status__alert">
                  Email is not configured on Cloud Run. Add <code>RESEND_API_KEY</code> (or SMTP) and redeploy the backend.
                </p>
              ) : null}
              {emailLog ? (
                <p className={`admin-chat-report-email-status__line${emailLog.sent ? ' is-sent' : ' is-failed'}`}>
                  {emailLog.sent
                    ? `Delivered to ${emailLog.to}${emailLog.provider ? ` via ${emailLog.provider}` : ''} · ${formatTimestamp(emailLog.at)}`
                    : `Not delivered to ${emailLog.to}${emailLog.reason ? ` (${emailLog.reason})` : ''}`}
                </p>
              ) : (
                <p className="admin-chat-report-email-status__line">No delivery record yet.</p>
              )}
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={resendingEmail || !emailConfigured}
                onClick={() => onResendEmail(report.id)}
              >
                <i className={`fas fa-paper-plane${resendingEmail ? ' fa-spin' : ''}`}></i>
                {resendingEmail ? ' Sending…' : ' Resend to reporter'}
              </button>
            </div>
          ) : null}

          <div className="admin-chat-report-modal__actions">
            {renderActions(report)}
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminChatReportsPanel = ({ addToast, onQueueChange }) => {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ open: 0, reviewing: 0, resolved: 0, dismissed: 0, total: 0, categories: {} });
  const [rangeCounts, setRangeCounts] = useState({ all: 0, day: 0, week: 0, month: 0, year: 0 });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
    hasPrev: false,
    hasNext: false,
    startIndex: 0,
    endIndex: 0,
  });
  const [statusFilter, setStatusFilter] = useState('open');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [chartType, setChartType] = useState('pie');
  const [filedDateMode, setFiledDateMode] = useState('all');
  const [filedDateYear, setFiledDateYear] = useState('');
  const [filedDateMonth, setFiledDateMonth] = useState('');
  const [filedDateDay, setFiledDateDay] = useState('');
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const preserveSelectedRef = useRef(null);

  const chartMetrics = useMemo(() => ({
    open: stats.open || 0,
    reviewing: stats.reviewing || 0,
    resolved: stats.resolved || 0,
    dismissed: stats.dismissed || 0,
  }), [stats]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.listConversationReportsForAdmin({
        status: statusFilter,
        search: searchQuery,
        page,
        limit: PAGE_SIZE,
        dateMode: filedDateMode,
        dateYear: filedDateYear,
        dateMonth: filedDateMonth,
        dateDay: filedDateDay,
      });
      const payload = res.data || {};
      setRows(Array.isArray(payload.items) ? payload.items : []);
      setStats(payload.stats || { open: 0, reviewing: 0, resolved: 0, dismissed: 0, total: 0, categories: {} });
      setRangeCounts(payload.rangeCounts || { all: 0, day: 0, week: 0, month: 0, year: 0 });
      setPagination(payload.pagination || {
        page: 1,
        limit: PAGE_SIZE,
        total: 0,
        totalPages: 1,
        hasPrev: false,
        hasNext: false,
        startIndex: 0,
        endIndex: 0,
      });
      if (payload.emailStatus) {
        setEmailConfigured(Boolean(payload.emailStatus.configured));
      }
    } catch (err) {
      addToast(err.message || 'Could not load chat reports', 'error');
    } finally {
      setLoading(false);
      onQueueChange?.();
    }
  };

  const loadDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await apiClient.getConversationReportForAdmin(id);
      setDetail(res.data || null);
    } catch (err) {
      addToast(err.message || 'Could not load report details', 'error');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [statusFilter, searchQuery, page, filedDateMode, filedDateYear, filedDateMonth, filedDateDay]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId]);

  useEffect(() => {
    if (preserveSelectedRef.current) {
      setSelectedId(preserveSelectedRef.current);
      preserveSelectedRef.current = null;
    }
  }, [rows]);

  const beginStatusUpdate = (id, status, confirmMessage) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setPendingAction({ id, status });
  };

  const submitStatusUpdate = async ({ notes, resolutionType, blockReportedUser }) => {
    if (!pendingAction) return;
    const { id, status } = pendingAction;
    setUpdatingId(id);
    try {
      const res = await apiClient.updateConversationReportForAdmin(id, {
        status,
        notes,
        resolutionType,
        blockReportedUser,
      });
      const updated = res.data;
      const labels = {
        reviewing: 'Report marked as reviewing.',
        resolved: 'Report resolved.',
        dismissed: 'Report dismissed.',
        open: 'Report re-opened.',
      };
      addToast(labels[status] || 'Report updated.', 'success');
      if (updated?.reporterEmailLog && !updated.reporterEmailLog.sent) {
        addToast('Reporter was not emailed — check Cloud Run email settings (RESEND_API_KEY or SMTP).', 'error');
      } else if (updated?.reporterEmailLog?.sent) {
        addToast(`Reporter notified at ${updated.reporterEmailLog.to}.`, 'success');
      }
      preserveSelectedRef.current = id;
      if (status === 'reviewing' || status === 'resolved' || status === 'dismissed' || status === 'open') {
        setStatusFilter(status === 'open' ? 'open' : status);
      }
      setPendingAction(null);
      await load();
      setSelectedId(id);
      await loadDetail(id);
    } catch (err) {
      addToast(err.message || 'Could not update report', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleCsvExport = async () => {
    setExportingCsv(true);
    try {
      const totalReports = rangeCounts.all || 0;
      if (!totalReports) {
        addToast('No chat reports to export.', 'error');
        return;
      }
      await apiClient.downloadConversationReportsExport();
      addToast(
        `Exported all ${totalReports.toLocaleString()} reports (full catalog). Filter in Excel as needed.`,
        'success'
      );
    } catch (err) {
      addToast(err.message || 'Could not export chat reports', 'error');
    } finally {
      setExportingCsv(false);
    }
  };

  const browseFiltersActive = statusFilter !== 'all'
    || searchQuery.trim() !== ''
    || filedDateMode !== 'all';

  const filedDateChartLabel = filedDateMode === 'all'
    ? 'All time'
    : filedDateMode === 'year' && filedDateYear
      ? filedDateYear
      : filedDateMode === 'month' && filedDateMonth
        ? filedDateMonth
        : filedDateMode === 'date' && filedDateDay
          ? filedDateDay
          : 'Selected period';

  const handleResendEmail = async (id) => {
    setResendingEmail(true);
    try {
      const res = await apiClient.resendConversationReportEmail(id);
      const result = res.data?.result;
      if (result?.sent) {
        addToast(`Email sent to ${result.log?.to || 'reporter'}.`, 'success');
      } else {
        addToast(result?.reason || 'Email could not be sent. Configure RESEND_API_KEY or SMTP on Cloud Run.', 'error');
      }
      await loadDetail(id);
      await load();
    } catch (err) {
      addToast(err.message || 'Could not resend email', 'error');
    } finally {
      setResendingEmail(false);
    }
  };

  const statCount = (key) => {
    if (key === 'all') return stats.total || 0;
    return stats[key] || 0;
  };

  const selectedRow = rows.find((r) => r.id === selectedId) || (detail?.id === selectedId ? detail : null);

  const renderActions = (row) => {
    const busy = updatingId === row.id;
    if (row.status === 'open') {
      return (
        <>
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => beginStatusUpdate(row.id, 'reviewing')}>
            Start review
          </button>
          <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={() => beginStatusUpdate(row.id, 'resolved', 'Mark this report as resolved?')}>
            Resolve
          </button>
          <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} disabled={busy} onClick={() => beginStatusUpdate(row.id, 'dismissed', 'Dismiss this report?')}>
            Dismiss
          </button>
        </>
      );
    }
    if (row.status === 'reviewing') {
      return (
        <>
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => beginStatusUpdate(row.id, 'resolved', 'Mark this report as resolved?')}>
            Resolve
          </button>
          <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} disabled={busy} onClick={() => beginStatusUpdate(row.id, 'dismissed', 'Dismiss this report?')}>
            Dismiss
          </button>
          <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={() => beginStatusUpdate(row.id, 'open', 'Re-open this report?')}>
            Re-open
          </button>
        </>
      );
    }
    return (
      <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={() => beginStatusUpdate(row.id, 'open', 'Re-open this report?')}>
        Re-open
      </button>
    );
  };

  return (
    <div>
      <div className="admin-panel-head">
        <div>
          <h3><i className="fas fa-flag"></i> Chat reports</h3>
          <p className="admin-panel-sub">
            Compact queue — click a row to open the full report, transcript, and actions.
            CSV export includes the <strong>full catalog</strong> for analytics (table filters are for browsing only).
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={handleCsvExport}
            disabled={exportingCsv || loading || !(rangeCounts.all > 0)}
            title="Download every chat report for Excel analysis"
          >
            <i className={`fas fa-download${exportingCsv ? ' fa-spin' : ''}`}></i> Download CSV (all)
          </button>
          <button type="button" className="btn btn-outline btn-sm" onClick={load} disabled={loading}>
            <i className={`fas fa-sync${loading ? ' fa-spin' : ''}`}></i> Refresh
          </button>
        </div>
      </div>

      {!emailConfigured ? (
        <div className="admin-email-warning" role="status">
          <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
          Reporter emails are off until you set <strong>RESEND_API_KEY</strong> or SMTP credentials on Cloud Run, then redeploy the backend.
        </div>
      ) : null}

      <div className="live-listings-toolbar" style={{ marginBottom: '1rem' }}>
        <BrowseListedDateFilter
          variant="admin"
          useCalendarInputs
          fieldLabel="Filed"
          yearMin={COOWN_PROJECT_START_YEAR}
          dateMode={filedDateMode}
          onDateModeChange={(mode) => { setFiledDateMode(mode); setPage(1); }}
          listedYear={filedDateYear}
          onListedYearChange={(v) => { setFiledDateYear(v); setPage(1); }}
          listedMonth={filedDateMonth}
          onListedMonthChange={(v) => { setFiledDateMonth(v); setPage(1); }}
          listedDate={filedDateDay}
          onListedDateChange={(v) => { setFiledDateDay(v); setPage(1); }}
          onClear={() => {
            setFiledDateMode('all');
            setFiledDateYear('');
            setFiledDateMonth('');
            setFiledDateDay('');
            setPage(1);
          }}
        />
        <p className="admin-chat-reports-period-hint" style={{ marginTop: '0.65rem', marginBottom: 0 }}>
          {filedDateMode === 'all'
            ? `Browsing all ${(rangeCounts.all || 0).toLocaleString()} reports. Use the calendar to narrow by filing date.`
            : `${(stats.total || 0).toLocaleString()} reports match the selected filing date. Status filters below apply to this period.`}
        </p>
      </div>

      <div className="platform-activity-layout admin-chat-reports-analytics">
        <div
          className="admin-chat-reports-status-filters"
          role="group"
          aria-label="Filter reports by status"
        >
          {STATUS_FILTERS.map((box) => (
            <button
              key={box.key}
              type="button"
              className={`admin-chat-reports-status-btn${statusFilter === box.key ? ' active' : ''}`}
              onClick={() => { setStatusFilter(box.key); setPage(1); }}
            >
              <span className="admin-chat-reports-status-btn__label">
                <i className={`fas ${box.icon}`} aria-hidden="true"></i>
                {box.label}
              </span>
              <span className="admin-chat-reports-status-btn__value">{statCount(box.key).toLocaleString()}</span>
            </button>
          ))}
        </div>
        <PlatformActivityChart
          metrics={chartMetrics}
          metricDefs={STATUS_METRICS}
          chartType={chartType}
          onChartTypeChange={setChartType}
          title={`Reports by status · ${filedDateChartLabel}`}
          emptyMessage="No chat reports in this period."
          ariaLabel="Chat reports status breakdown"
          colorMap={STATUS_CHART_COLORS}
        />
      </div>

      {browseFiltersActive ? (
        <p className="live-listings-analytics-note">
          Table shows <strong>{pagination.total.toLocaleString()}</strong> reports matching your status
          {searchQuery.trim() ? ' and search' : ''} filters.
          CSV export still includes the full catalog ({(rangeCounts.all || 0).toLocaleString()}).
        </p>
      ) : null}

      <div className="submission-queue-toolbar" style={{ marginBottom: '1rem' }}>
        <div className="submission-queue-search">
          <div className="submission-queue-search-wrap">
            <i className="fas fa-search" aria-hidden="true"></i>
            <input
              type="text"
              inputMode="search"
              autoComplete="off"
              spellCheck={false}
              placeholder="Search property, email, or reason…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search chat reports"
            />
            {searchInput.trim() ? (
              <button
                type="button"
                className="submission-queue-search-clear"
                aria-label="Clear search"
                onClick={() => setSearchInput('')}
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="investment-table investment-table--chat-reports">
          <thead>
            <tr>
              <th>Report</th>
              <th>Category</th>
              <th>Reporter</th>
              <th>Reported</th>
              <th>Status</th>
              <th>Reviewer</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                  Loading chat reports…
                </td>
              </tr>
            ) : null}
            {!loading && rows.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
                  {searchQuery.trim()
                    ? 'No reports match your search.'
                    : statusFilter === 'open'
                      ? 'No open chat reports.'
                      : 'No reports in this queue.'}
                </td>
              </tr>
            ) : null}
            {rows.map((row) => (
              <tr
                key={row.id}
                className={`admin-chat-report-row${selectedId === row.id ? ' is-selected' : ''}`}
                onClick={() => setSelectedId(row.id)}
              >
                <td className="live-listing-deal-cell">
                  <div className="live-listing-deal-line">
                    <strong className="live-listing-deal-title">{row.propertyTitle || 'Listing conversation'}</strong>
                  </div>
                  <div className="live-listing-deal-sub admin-chat-report-row__reason">{row.reason}</div>
                </td>
                <td>{CATEGORY_LABELS[row.category] || row.category}</td>
                <td className="admin-chat-report-row__email">{row.reporterEmail || '—'}</td>
                <td>{formatShortDate(row.createdAt)}</td>
                <td><span className={statusBadgeClass(row.status)}>{STATUS_LABELS[row.status] || row.status}</span></td>
                <td className="admin-chat-report-row__email">{row.reviewerEmail || '—'}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm live-listing-manage-btn"
                    onClick={(e) => { e.stopPropagation(); setSelectedId(row.id); }}
                  >
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length > 0 ? (
        <PaginationBar
          page={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          startIndex={pagination.startIndex}
          endIndex={pagination.endIndex}
          hasPrev={pagination.hasPrev}
          hasNext={pagination.hasNext}
          onPageChange={setPage}
          itemLabel="reports"
          compact
        />
      ) : null}

      <ReportDetailModal
        report={selectedRow}
        detail={detail}
        detailLoading={detailLoading}
        busy={Boolean(updatingId)}
        emailConfigured={emailConfigured}
        resendingEmail={resendingEmail}
        onClose={() => setSelectedId(null)}
        onResendEmail={handleResendEmail}
        renderActions={renderActions}
      />

      <ReviewNotesModal
        action={pendingAction}
        busy={Boolean(updatingId)}
        onClose={() => setPendingAction(null)}
        onSubmit={submitStatusUpdate}
      />
    </div>
  );
};

export default AdminChatReportsPanel;
